import React from 'react';
import PropTypes from 'prop-types';
import { X } from 'lucide-react';
import IconButton from '../../ui/IconButton';
import Tooltip from '../../ui/Tooltip';
import { cx } from '../../ui/classNames';
import {
    CountryChipLabel,
    getCountryChipDisplay,
} from '../../shared/securityDisplayPrimitives';
import { AddingBadge } from './AddSecurityRow';

// A quiet dot between the parts (MUI's disabled text colour).
const separator = (
    <span aria-hidden="true" className="shrink-0 text-black/[0.38] dark:text-white/50">·</span>
);

const ELLIPSIS_CLASS_NAME = 'min-w-0 overflow-hidden text-ellipsis whitespace-nowrap';
// The remove button: the kit's small frame in the secondary text colour,
// the ink under a pointer (the disabled colours stay the kit's), centred on
// its line as MUI's buttons are.
const REMOVE_BUTTON_CLASS_NAME = 'enabled:!text-content-secondary enabled:hover:!text-ui-ink';

/**
 * A pending addition's identity in its grid row: "AAPL · Apple Inc." and the
 * listing's country, the Adding badge ("Out of date" once Save reports the
 * choice stale) and a button that takes it out of the additions.
 */
export default function PendingAdditionCell({ pick, onRemove, disabled = false }) {
    const name = pick.ticker || pick.label;
    return (
        <div className="flex w-full min-w-0 items-center gap-1.5">
            <span
                title={[pick.label, pick.country].filter(Boolean).join(' · ')}
                className="flex min-w-0 items-center gap-1 [line-height:normal]"
            >
                <span className={cx(ELLIPSIS_CLASS_NAME, 'max-w-full shrink-0 font-bold')}>
                    {name}
                </span>
                {pick.ticker && pick.companyName && (
                    <>
                        {separator}
                        <span className={ELLIPSIS_CLASS_NAME}>{pick.companyName}</span>
                    </>
                )}
                {pick.country && (
                    <>
                        {separator}
                        <span className="shrink-0 text-content-secondary">
                            <CountryChipLabel display={getCountryChipDisplay(pick.country)} />
                        </span>
                    </>
                )}
            </span>
            <AddingBadge stale={Boolean(pick.stale)} unresolved={pick.kind === 'unresolved'} />
            <Tooltip content="Don't add">
                <span>
                    <IconButton
                        size="xs"
                        label={`Don't add ${pick.label}`}
                        onClick={() => onRemove(pick.key)}
                        disabled={disabled}
                        className={REMOVE_BUTTON_CLASS_NAME}
                    >
                        <X aria-hidden="true" size={16} />
                    </IconButton>
                </span>
            </Tooltip>
        </div>
    );
}

PendingAdditionCell.propTypes = {
    pick: PropTypes.shape({
        key: PropTypes.string.isRequired,
        kind: PropTypes.oneOf(['listing', 'unresolved']).isRequired,
        label: PropTypes.string.isRequired,
        ticker: PropTypes.string,
        companyName: PropTypes.string,
        country: PropTypes.string,
        stale: PropTypes.bool,
    }).isRequired,
    onRemove: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
};
